import EmailEditorController from "ui_components/controllers/email_editor_controller"

const NON_RESIZABLE_TYPES = new Set(["wrapper", "textnode", "comment"])
const NON_RESIZABLE_TAGS = new Set(["html", "body", "thead", "tbody", "tfoot", "tr"])

const RESIZE_HANDLES = {
  tl: 1,
  tc: 1,
  tr: 1,
  cl: 1,
  cr: 1,
  bl: 1,
  bc: 1,
  br: 1,
  keyWidth: "width",
  keyHeight: "height",
}

export default class extends EmailEditorController {
  _loadContent() {
    if (this._isViewTemplateEditor()) {
      this.initialHtmlValue = this._removeRelativeImagePreloads(this.initialHtmlValue)
      this.initialJsonValue = this._removeRelativeImagePreloadsFromJson(this.initialJsonValue)
      if (this.hasSourceInputTarget) this.sourceInputTarget.value = this._removeRelativeImagePreloads(this.sourceInputTarget.value)
      if (this.hasHtmlInputTarget) this.htmlInputTarget.value = this._removeRelativeImagePreloads(this.htmlInputTarget.value)
    }
    super._loadContent()
  }

  _removeRelativeImagePreloads(html) {
    return html.replace(/<link\b[^>]*>/gi, (tag) => {
      const relativeImage = /\bhref\s*=\s*["'](?:\.\/)?images\//i.test(tag)
      const preload = /\brel\s*=\s*["']preload["']/i.test(tag)
      const image = /\bas\s*=\s*["']image["']/i.test(tag)
      return relativeImage && preload && image ? "" : tag
    })
  }

  _removeRelativeImagePreloadsFromJson(raw) {
    if (!raw) return raw

    try {
      let project = JSON.parse(raw)
      while (typeof project === "string") project = JSON.parse(project)

      const clean = (node) => {
        if (Array.isArray(node)) {
          return node.filter((item) => {
            const attributes = item?.attributes
            return !(String(attributes?.rel || "").toLowerCase() === "preload" &&
              String(attributes?.as || "").toLowerCase() === "image" &&
              /^(?:\.\/)?images\//i.test(attributes?.href || ""))
          }).map(clean)
        }
        if (node && typeof node === "object") {
          Object.keys(node).forEach((key) => { node[key] = clean(node[key]) })
        }
        return node
      }

      return JSON.stringify(clean(project))
    } catch {
      return raw
    }
  }

  _initEditor() {
    super._initEditor()

    if (!this.editor || !this._isViewTemplateEditor()) return

    this.editor.on("component:selected", (component) => {
      this._enableSelectedComponentResize(component)
      this._syncFreePosition(component)
    })
    this.editor.on("component:styleUpdate:position", (component) => {
      requestAnimationFrame(() => {
        if (this.editor) this._syncFreePosition(component)
      })
    })
    this.editor.on("style:property:update", () => {
      requestAnimationFrame(() => {
        if (this.editor) this._syncFreePosition(this.editor.getSelected())
      })
    })
    this.editor.on("component:drag:end", ({target}) => {
      this._constrainToParent(target)
    })
    this.editor.on("component:resize", (payload) => {
      this._scheduleParentConstraint(payload?.model || payload?.target || payload)
    })
  }

  _syncFreePosition(component) {
    if (!this._componentCanBeResized(component)) return

    const element = component.getEl()
    if (!element) return
    const view = element.ownerDocument.defaultView
    const free = view.getComputedStyle(element).position === "absolute"
    const parent = component.parent()
    const parentElement = parent?.getEl()
    if (free && parentElement) {
      const parentStyle = view.getComputedStyle(parentElement)
      const containmentStyle = {overflow: "hidden"}
      if (parentStyle.position === "static") containmentStyle.position = "relative"
      parent.addStyle(containmentStyle)
    }

    // O pai precisa ser a referência antes de o GrapesJS calcular o arraste.
    component.setDragMode(free ? "absolute" : "")
    if (free) this._scheduleParentConstraint(component)
  }

  _scheduleParentConstraint(component) {
    cancelAnimationFrame(this.parentConstraintFrame)
    this.parentConstraintFrame = requestAnimationFrame(() => {
      this._constrainToParent(component)
    })
  }

  _constrainToParent(component) {
    if (!component?.getStyle || component.getStyle()?.position !== "absolute") return

    const element = component.getEl()
    const parentElement = component.parent()?.getEl()
    if (!element || !parentElement) return

    const elementRect = element.getBoundingClientRect()
    const parentRect = parentElement.getBoundingClientRect()
    const maxLeft = Math.max(0, parentElement.clientWidth - elementRect.width)
    const maxTop = Math.max(0, parentElement.clientHeight - elementRect.height)
    const left = Math.min(maxLeft, Math.max(0, elementRect.left - parentRect.left - parentElement.clientLeft))
    const top = Math.min(maxTop, Math.max(0, elementRect.top - parentRect.top - parentElement.clientTop))

    component.addStyle({
      left: `${Math.round(left)}px`,
      top: `${Math.round(top)}px`,
      right: "auto",
      bottom: "auto",
    })
  }

  _enableSelectedComponentResize(component) {
    if (!this._componentCanBeResized(component)) return

    // Imagens possuem uma configuracao proporcional propria do GrapesJS.
    if (!component.get("resizable")) {
      component.set("resizable", {...RESIZE_HANDLES})
    }
  }

  _componentCanBeResized(component) {
    if (!component || component.get("selectable") === false) return false

    const type = component.get("type") || ""
    const tagName = (component.get("tagName") || "").toLowerCase()

    return !NON_RESIZABLE_TYPES.has(type) && !NON_RESIZABLE_TAGS.has(tagName)
  }

  _isViewTemplateEditor() {
    return Boolean(
      this.element.closest("form")?.querySelector('[name^="view_template["]')
    )
  }

  async _fetchPreview() {
    const viewId = this.element
      .closest("form")
      ?.querySelector('[name="view_template[view_id]"]')
      ?.value

    if (!viewId) return super._fetchPreview()

    const originalUrl = this.previewUrlValue
    const previewUrl = new URL(originalUrl, window.location.origin)
    previewUrl.searchParams.set("view_id", viewId)
    this.previewUrlValue = `${previewUrl.pathname}${previewUrl.search}`

    try {
      return await super._fetchPreview()
    } finally {
      this.previewUrlValue = originalUrl
    }
  }

  _getStyleManagerConfig() {
    const config = super._getStyleManagerConfig()
    if (!this._isViewTemplateEditor()) return config

    const general = config.sectors.find((sector) => sector.name === "Geral")

    if (general) {
      general.buildProps = [
        "width",
        "height",
        "min-width",
        "min-height",
        "max-width",
        "max-height",
        "padding",
        "margin",
        "background-color",
        "text-align",
      ]
    }

    // O editor usa a API legada buildProps; assim as propriedades nativas do
    // GrapesJS aparecem corretamente e respeitam o elemento selecionado.
    config.sectors.push(
      {
        name: "Alinhamento do conteúdo",
        open: true,
        buildProps: ["display", "flex-direction", "justify-content", "align-items", "gap"],
        properties: [
          {
            name: "Organização",
            property: "display",
            type: "select",
            default: "block",
            options: [
              {id: "block", label: "Normal"},
              {id: "flex", label: "Flexível"},
              {id: "inline", label: "Na mesma linha"},
              {id: "none", label: "Oculto"},
            ],
          },
          {
            name: "Direção",
            property: "flex-direction",
            type: "select",
            default: "row",
            options: [
              {id: "row", label: "Horizontal"},
              {id: "column", label: "Vertical"},
              {id: "row-reverse", label: "Horizontal invertida"},
              {id: "column-reverse", label: "Vertical invertida"},
            ],
          },
          {
            name: "Posição na direção escolhida",
            property: "justify-content",
            type: "select",
            default: "flex-start",
            options: [
              {id: "flex-start", label: "Início"},
              {id: "center", label: "Centro"},
              {id: "flex-end", label: "Fim"},
              {id: "space-between", label: "Espaço entre"},
              {id: "space-around", label: "Espaço ao redor"},
              {id: "space-evenly", label: "Distribuir igualmente"},
            ],
          },
          {
            name: "Posição na outra direção",
            property: "align-items",
            type: "select",
            default: "stretch",
            options: [
              {id: "stretch", label: "Preencher espaço"},
              {id: "flex-start", label: "Início"},
              {id: "center", label: "Centro"},
              {id: "flex-end", label: "Fim"},
              {id: "baseline", label: "Alinhar pelo texto"},
            ],
          },
          {
            name: "Espaço entre elementos",
            property: "gap",
            type: "integer",
            units: ["px", "rem"],
            default: 0,
            min: 0,
          },
        ],
      },
      {
        name: "Posicionamento",
        open: true,
        buildProps: ["position", "top", "right", "bottom", "left", "z-index"],
        properties: [
          {
            name: "Modo",
            property: "position",
            type: "select",
            default: "static",
            options: [
              {id: "static", label: "Normal"},
              {id: "relative", label: "Relativo à posição normal"},
              {id: "absolute", label: "Livre dentro da div"},
            ],
          },
          {name: "Distância do topo", property: "top"},
          {name: "Distância da direita", property: "right"},
          {name: "Distância inferior", property: "bottom"},
          {name: "Distância da esquerda", property: "left"},
          {name: "Ordem de sobreposição", property: "z-index"},
        ],
      }
    )

    return config
  }
}
